import React from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import {
  MdFolder,
  MdFolderOpen,
  MdChevronRight,
  MdExpandMore,
  MdMoreVert,
  MdHome,
  MdDragIndicator,
  MdLock,
  MdLockOpen,
} from "react-icons/md";
import InlineRename from "components/ui/InlineRename";
import type { CourseFolder } from "services/instructorCourseFoldersService";

export type FolderNode = CourseFolder & { children?: FolderNode[] };

export type FolderTreeHandlers = {
  onSelect: (folderId: number | null) => void;
  onToggle: (folderId: number) => void;
  onRename: (folder: CourseFolder, nextTitle: string) => Promise<void> | void;
  onOpenMenu: (folder: CourseFolder, anchor: HTMLElement) => void;
  onAddChild: (parent: CourseFolder) => void;
};

export type FolderTreeProps = FolderTreeHandlers & {
  tree: FolderNode[];
  expanded: Set<number>;
  selectedId: number | null;
  rtl?: boolean;
  /**
   * The id of the draggable currently in flight (e.g. `folder-drag-12`).
   * Used to dim the source row while dragging so the DragOverlay ghost is
   * the primary visual.
   */
  activeDragId?: string | null;
};

export type FolderBetweenData = {
  kind: "folder-between";
  anchorId: number;
  position: "above" | "below";
  parentId: number | null;
};

export type FolderInsideData = {
  kind: "folder-inside";
  folderId: number;
};

export type FolderRootData = {
  kind: "folder-root";
};

function BetweenStrip({
  id,
  data,
  rtl,
  depth,
}: {
  id: string;
  data: FolderBetweenData;
  rtl?: boolean;
  depth: number;
}) {
  const { setNodeRef, isOver } = useDroppable({ id, data });
  const inset = depth * 14 + 32;
  const indicatorStyle: React.CSSProperties = rtl
    ? { right: `${inset}px`, left: "8px" }
    : { left: `${inset}px`, right: "8px" };
  return (
    <div ref={setNodeRef} className="relative h-1.5">
      {isOver && (
        <div
          style={indicatorStyle}
          className="pointer-events-none absolute top-1/2 z-10 h-0.5 -translate-y-1/2 rounded-full bg-brand-500 shadow-[0_0_0_2px_rgba(67,_24,_255,_0.12)]"
        />
      )}
    </div>
  );
}

function FolderRow({
  node,
  depth,
  expanded,
  selectedId,
  rtl,
  handlers,
  activeDragId,
}: {
  node: FolderNode;
  depth: number;
  expanded: Set<number>;
  selectedId: number | null;
  rtl?: boolean;
  handlers: FolderTreeHandlers;
  activeDragId?: string | null;
}) {
  const isOpen = expanded.has(node.id);
  const isSelected = selectedId === node.id;
  const isDraggedSelf = activeDragId === `folder-drag-${node.id}`;

  const insideDrop = useDroppable({
    id: `folder-inside-${node.id}`,
    data: { kind: "folder-inside", folderId: node.id } as FolderInsideData,
  });
  const draggable = useDraggable({
    id: `folder-drag-${node.id}`,
    data: { kind: "folder", folderId: node.id, parentId: node.parent_id ?? null },
  });

  const padStart = rtl ? undefined : `${depth * 14 + 4}px`;
  const padEnd = rtl ? `${depth * 14 + 4}px` : undefined;

  const showNestRing = insideDrop.isOver && !isDraggedSelf;

  return (
    <li>
      <div
        ref={(el) => {
          insideDrop.setNodeRef(el);
          draggable.setNodeRef(el);
        }}
        onClick={() => handlers.onSelect(node.id)}
        style={{
          paddingLeft: padStart,
          paddingRight: padEnd,
          opacity: isDraggedSelf ? 0.4 : 1,
        }}
        className={`group flex items-center gap-1 rounded-lg py-1 pr-1 text-sm transition-all ${
          isSelected
            ? "bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-200"
            : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10"
        } ${
          showNestRing
            ? "bg-brand-50/70 ring-2 ring-brand-400 dark:bg-brand-500/20"
            : ""
        }`}
      >
        <button
          ref={draggable.setActivatorNodeRef as React.Ref<HTMLButtonElement>}
          {...draggable.attributes}
          {...draggable.listeners}
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="flex h-6 w-5 shrink-0 cursor-grab items-center justify-center rounded text-gray-300 opacity-60 transition-opacity hover:bg-gray-200 hover:text-gray-500 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 active:cursor-grabbing dark:text-gray-500 dark:hover:bg-white/10 dark:hover:text-gray-300"
          aria-label={rtl ? "اسحب المجلد" : "Drag folder"}
        >
          <MdDragIndicator className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handlers.onToggle(node.id);
          }}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10"
          aria-label="Toggle"
        >
          {(node.children && node.children.length > 0) || (node.children_count ?? 0) > 0 ? (
            isOpen ? (
              <MdExpandMore className="h-4 w-4" />
            ) : (
              <MdChevronRight className={`h-4 w-4 ${rtl ? "rotate-180" : ""}`} />
            )
          ) : (
            <span className="h-4 w-4" />
          )}
        </button>
        <button
          type="button"
          onClick={() => handlers.onSelect(node.id)}
          className="flex min-w-0 flex-1 items-center gap-1.5 text-start"
        >
          {isOpen ? (
            <MdFolderOpen className="h-4 w-4 shrink-0 text-amber-500" />
          ) : (
            <MdFolder className="h-4 w-4 shrink-0 text-amber-500" />
          )}
          <InlineRename
            value={node.title}
            onSubmit={async (next) => {
              await handlers.onRename(node, next);
            }}
          >
            {({ edit, label }) => (
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <span
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    edit();
                  }}
                  className="min-w-0 flex-1 truncate"
                >
                  {label}
                </span>
                <div className="flex shrink-0 items-center gap-1 text-[11px] font-semibold text-gray-400">
                  {(node.children_count ?? 0) > 0 && (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 dark:bg-white/10">
                      {node.children_count}
                    </span>
                  )}
                  {(node.items_count ?? 0) > 0 && (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 dark:bg-white/10">
                      {node.items_count}
                    </span>
                  )}
                </div>
              </div>
            )}
          </InlineRename>
          {node.is_locked !== false ? (
            <MdLock className="h-3.5 w-3.5 shrink-0 text-amber-500" title="مغلق للمشاهدة" />
          ) : (
            <MdLockOpen className="h-3.5 w-3.5 shrink-0 text-emerald-500" title="مفتوح للمشاهدة" />
          )}
        </button>
        {/* Per-folder "add sub-folder" icon intentionally removed (use the main add button). */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handlers.onOpenMenu(node, e.currentTarget);
          }}
          className="rounded p-1 text-gray-400 opacity-60 transition-opacity hover:bg-gray-200 group-hover:opacity-100 focus:opacity-100 dark:hover:bg-white/10"
          aria-label="More"
        >
          <MdMoreVert className="h-4 w-4" />
        </button>
      </div>
      {isOpen && node.children && node.children.length > 0 && (
        <FolderList
          nodes={node.children}
          depth={depth + 1}
          expanded={expanded}
          selectedId={selectedId}
          rtl={rtl}
          handlers={handlers}
          parentId={node.id}
          activeDragId={activeDragId}
        />
      )}
    </li>
  );
}

function FolderList({
  nodes,
  depth,
  expanded,
  selectedId,
  rtl,
  handlers,
  parentId,
  activeDragId,
}: {
  nodes: FolderNode[];
  depth: number;
  expanded: Set<number>;
  selectedId: number | null;
  rtl?: boolean;
  handlers: FolderTreeHandlers;
  parentId: number | null;
  activeDragId?: string | null;
}) {
  if (nodes.length === 0) return null;
  const last = nodes[nodes.length - 1];
  return (
    <ul className="flex flex-col">
      {nodes.map((node) => (
        <React.Fragment key={node.id}>
          <BetweenStrip
            id={`folder-above-${node.id}`}
            data={{
              kind: "folder-between",
              anchorId: node.id,
              position: "above",
              parentId,
            }}
            rtl={rtl}
            depth={depth}
          />
          <FolderRow
            node={node}
            depth={depth}
            expanded={expanded}
            selectedId={selectedId}
            rtl={rtl}
            handlers={handlers}
            activeDragId={activeDragId}
          />
        </React.Fragment>
      ))}
      <BetweenStrip
        id={`folder-below-${last.id}`}
        data={{
          kind: "folder-between",
          anchorId: last.id,
          position: "below",
          parentId,
        }}
        rtl={rtl}
        depth={depth}
      />
    </ul>
  );
}

export default function FolderTree({
  tree,
  expanded,
  selectedId,
  rtl,
  onSelect,
  onToggle,
  onRename,
  onOpenMenu,
  onAddChild,
  activeDragId,
}: FolderTreeProps) {
  const handlers: FolderTreeHandlers = {
    onSelect,
    onToggle,
    onRename,
    onOpenMenu,
    onAddChild,
  };
  const rootDrop = useDroppable({
    id: "folder-drop-root",
    data: { kind: "folder-root" } as FolderRootData,
  });

  return (
    <div className="flex flex-col gap-1">
      <div
        ref={rootDrop.setNodeRef}
        className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium transition-all ${
          selectedId === null
            ? "bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-200"
            : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10"
        } ${rootDrop.isOver ? "ring-2 ring-brand-400" : ""}`}
      >
        <button
          type="button"
          onClick={() => onSelect(null)}
          className="flex flex-1 items-center gap-2"
        >
          <MdHome className="h-4 w-4" />
          <span>{rtl ? "الجذر" : "Root"}</span>
        </button>
      </div>
      <FolderList
        nodes={tree}
        depth={0}
        expanded={expanded}
        selectedId={selectedId}
        rtl={rtl}
        handlers={handlers}
        parentId={null}
        activeDragId={activeDragId}
      />
    </div>
  );
}
